import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Phone, FlaskConical, Scissors, ShieldCheck, IndianRupee, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const formatINR = (num) => {
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const formatINRFull = (num) => {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
};

const TABS = [
  { id: 'followup', label: 'Follow-Up', icon: Phone, color: 'sky' },
  { id: 'labs', label: 'Diagnostics', icon: FlaskConical, color: 'emerald' },
  { id: 'procedures', label: 'Procedures', icon: Scissors, color: 'violet' },
  { id: 'insurance', label: 'Insurance', icon: ShieldCheck, color: 'amber' },
];

const FollowUpCalculator = ({ isDark }) => {
  const [inputs, setInputs] = useState({
    opdPerDay: 300,
    consultationFee: 800,
    workingDays: 28,
  });

  const results = useMemo(() => {
    const { opdPerDay, consultationFee, workingDays } = inputs;
    const monthlyOPD = opdPerDay * workingDays;
    const followUpRate = 0.3;
    const followUpsAdvised = monthlyOPD * followUpRate;
    const withoutCompletion = 0.5;
    const withCompletion = 0.7;
    const completedWithout = followUpsAdvised * withoutCompletion;
    const completedWith = followUpsAdvised * withCompletion;
    const revenueWithout = completedWithout * consultationFee;
    const revenueWith = completedWith * consultationFee;
    const incrementalGain = revenueWith - revenueWithout;
    const percentGain = revenueWithout > 0 ? ((incrementalGain / revenueWithout) * 100) : 0;

    return {
      monthlyOPD,
      followUpsAdvised: Math.round(followUpsAdvised),
      completedWithout: Math.round(completedWithout),
      completedWith: Math.round(completedWith),
      revenueWithout: Math.round(revenueWithout),
      revenueWith: Math.round(revenueWith),
      incrementalGain: Math.round(incrementalGain),
      percentGain: Math.round(percentGain),
    };
  }, [inputs]);

  return (
    <CalculatorLayout
      isDark={isDark}
      inputs={inputs}
      setInputs={setInputs}
      sliders={[
        { key: 'opdPerDay', label: 'OPD Patients / Day', min: 50, max: 1000, step: 10, unit: 'patients' },
        { key: 'consultationFee', label: 'Avg Consultation Fee', min: 200, max: 2000, step: 50, unit: 'inr' },
        { key: 'workingDays', label: 'Working Days / Month', min: 20, max: 30, step: 1, unit: 'days' },
      ]}
      totalGain={results.incrementalGain}
      percentGain={results.percentGain}
      metrics={[
        { label: 'Monthly OPD', value: formatINRFull(results.monthlyOPD), sub: 'patients' },
        { label: 'Follow-ups Advised', value: formatINRFull(results.followUpsAdvised), sub: '30% of OPD' },
        { label: 'Completed (Current)', value: formatINRFull(results.completedWithout), sub: '50% completion' },
        { label: 'Completed (With Skydoc)', value: formatINRFull(results.completedWith), sub: '70% completion' },
      ]}
      comparison={{
        without: { label: 'Without Skydoc', value: results.revenueWithout },
        with: { label: 'With Skydoc', value: results.revenueWith },
      }}
      description="Skydoc's Follow-Up Agent auto-calls patients, reminds them of appointments, educates on follow-up importance, and books/reschedules. 50% of follow-ups are currently missed due to forgetfulness, no urgency, or booking friction."
    />
  );
};

const LabsCalculator = ({ isDark }) => {
  const [inputs, setInputs] = useState({
    opdPerDay: 300,
    workingDays: 26,
    testAdvisedRate: 40,
    avgDiagnosticValue: 2500,
  });

  const results = useMemo(() => {
    const { opdPerDay, workingDays, testAdvisedRate, avgDiagnosticValue } = inputs;
    const monthlyOPD = opdPerDay * workingDays;
    const totalTests = monthlyOPD * (testAdvisedRate / 100);
    const withoutCompletion = 0.5;
    const withCompletion = 0.7;
    const convertedWithout = totalTests * withoutCompletion;
    const convertedWith = totalTests * withCompletion;
    const revenueWithout = convertedWithout * avgDiagnosticValue;
    const revenueWith = convertedWith * avgDiagnosticValue;
    const uplift = revenueWith - revenueWithout;
    const percentIncrease = revenueWithout > 0 ? ((uplift / revenueWithout) * 100) : 0;

    return {
      monthlyOPD,
      totalTests: Math.round(totalTests),
      convertedWithout: Math.round(convertedWithout),
      convertedWith: Math.round(convertedWith),
      revenueWithout: Math.round(revenueWithout),
      revenueWith: Math.round(revenueWith),
      uplift: Math.round(uplift),
      percentIncrease: Math.round(percentIncrease),
    };
  }, [inputs]);

  return (
    <CalculatorLayout
      isDark={isDark}
      inputs={inputs}
      setInputs={setInputs}
      sliders={[
        { key: 'opdPerDay', label: 'OPD Patients / Day', min: 50, max: 1000, step: 10, unit: 'patients' },
        { key: 'workingDays', label: 'Working Days / Month', min: 20, max: 30, step: 1, unit: 'days' },
        { key: 'testAdvisedRate', label: '% Patients Advised Tests', min: 10, max: 80, step: 5, unit: 'percent' },
        { key: 'avgDiagnosticValue', label: 'Avg Diagnostic Value / Patient', min: 500, max: 10000, step: 250, unit: 'inr' },
      ]}
      totalGain={results.uplift}
      percentGain={results.percentIncrease}
      metrics={[
        { label: 'Tests Advised / Month', value: formatINRFull(results.totalTests), sub: `${inputs.testAdvisedRate}% of OPD` },
        { label: 'Completed (Current)', value: formatINRFull(results.convertedWithout), sub: '50% completion' },
        { label: 'Completed (With Skydoc)', value: formatINRFull(results.convertedWith), sub: '70% completion' },
        { label: 'Extra Tests Completed', value: `+${formatINRFull(results.convertedWith - results.convertedWithout)}`, sub: 'per month' },
      ]}
      comparison={{
        without: { label: 'Without Skydoc', value: results.revenueWithout },
        with: { label: 'With Skydoc', value: results.revenueWith },
      }}
      description="Tests advised but not completed, patients going to external labs, and delayed tests cause massive diagnostics revenue leakage. Skydoc extracts test orders, sends reminders, prioritizes critical tests, and routes patients to in-house labs."
    />
  );
};

const ProceduresCalculator = ({ isDark }) => {
  const [inputs, setInputs] = useState({
    monthlyOPD: 10000,
    advisedRate: 5,
    avgProcedureValue: 50000,
  });

  const results = useMemo(() => {
    const { monthlyOPD, advisedRate, avgProcedureValue } = inputs;
    const totalProposed = monthlyOPD * (advisedRate / 100);
    const withoutConversion = 0.4;
    const withConversion = 0.6;
    const plannedWithout = totalProposed * withoutConversion;
    const plannedWith = totalProposed * withConversion;
    const revenueWithout = plannedWithout * avgProcedureValue;
    const revenueWith = plannedWith * avgProcedureValue;
    const totalGain = revenueWith - revenueWithout;
    const percentGain = revenueWithout > 0 ? ((totalGain / revenueWithout) * 100) : 0;

    return {
      totalProposed: Math.round(totalProposed),
      plannedWithout: Math.round(plannedWithout),
      plannedWith: Math.round(plannedWith),
      revenueWithout: Math.round(revenueWithout),
      revenueWith: Math.round(revenueWith),
      totalGain: Math.round(totalGain),
      percentGain: Math.round(percentGain),
    };
  }, [inputs]);

  return (
    <CalculatorLayout
      isDark={isDark}
      inputs={inputs}
      setInputs={setInputs}
      sliders={[
        { key: 'monthlyOPD', label: 'Monthly OPD Patients', min: 1000, max: 50000, step: 500, unit: 'patients' },
        { key: 'advisedRate', label: '% Advised Procedures', min: 1, max: 15, step: 1, unit: 'percent' },
        { key: 'avgProcedureValue', label: 'Avg Procedure Value', min: 10000, max: 200000, step: 5000, unit: 'inr' },
      ]}
      totalGain={results.totalGain}
      percentGain={results.percentGain}
      metrics={[
        { label: 'Procedures Proposed', value: formatINRFull(results.totalProposed), sub: `${inputs.advisedRate}% of OPD` },
        { label: 'Converted (Current)', value: formatINRFull(results.plannedWithout), sub: '40% conversion' },
        { label: 'Converted (With Skydoc)', value: formatINRFull(results.plannedWith), sub: '60% conversion' },
        { label: 'Extra Procedures', value: `+${formatINRFull(results.plannedWith - results.plannedWithout)}`, sub: 'per month' },
      ]}
      comparison={{
        without: { label: 'Without Skydoc', value: results.revenueWithout },
        with: { label: 'With Skydoc', value: results.revenueWith },
      }}
      description="Procedures advised but not done due to fear, confusion, decision delays, and scheduling friction. Skydoc provides patient education, risk explanations, insurance pre-auth clarity, and faster OT scheduling to convert 40% to 60%."
    />
  );
};

const InsuranceCalculator = ({ isDark }) => {
  const [inputs, setInputs] = useState({
    monthlyOPD: 10000,
    insuranceRate: 25,
    avgBilling: 1500,
  });

  const results = useMemo(() => {
    const { monthlyOPD, insuranceRate, avgBilling } = inputs;
    const totalInsured = monthlyOPD * (insuranceRate / 100);
    const rejectionRate = 0.15;
    const approvalRateWith = 0.95;
    const totalRejected = totalInsured * rejectionRate;
    const rejectionLoss = totalRejected * avgBilling;
    const underuseRate = 0.10;
    const totalUnderused = totalInsured * underuseRate;
    const underuseLoss = totalUnderused * avgBilling;
    const totalApproved = totalInsured * (1 - rejectionRate);
    const approvedRevenue = totalApproved * avgBilling;
    const totalApprovalWith = monthlyOPD * (insuranceRate / 100) * approvalRateWith;
    const revenueWith = totalApprovalWith * avgBilling;
    const recoveredRevenue = revenueWith - approvedRevenue;
    const percentGain = approvedRevenue > 0 ? ((recoveredRevenue / approvedRevenue) * 100) : 0;

    return {
      totalInsured: Math.round(totalInsured),
      totalRejected: Math.round(totalRejected),
      rejectionLoss: Math.round(rejectionLoss),
      totalUnderused: Math.round(totalUnderused),
      underuseLoss: Math.round(underuseLoss),
      approvedRevenue: Math.round(approvedRevenue),
      revenueWith: Math.round(revenueWith),
      recoveredRevenue: Math.round(recoveredRevenue),
      percentGain: Math.round(percentGain),
    };
  }, [inputs]);

  return (
    <CalculatorLayout
      isDark={isDark}
      inputs={inputs}
      setInputs={setInputs}
      sliders={[
        { key: 'monthlyOPD', label: 'Monthly OPD Patients', min: 1000, max: 50000, step: 500, unit: 'patients' },
        { key: 'insuranceRate', label: '% Using Insurance / TPA', min: 5, max: 60, step: 5, unit: 'percent' },
        { key: 'avgBilling', label: 'Avg OPD Billing (Consult + Tests)', min: 500, max: 5000, step: 100, unit: 'inr' },
      ]}
      totalGain={results.recoveredRevenue}
      percentGain={results.percentGain}
      metrics={[
        { label: 'Insured Patients', value: formatINRFull(results.totalInsured), sub: `${inputs.insuranceRate}% of OPD` },
        { label: 'Claims Rejected (Current)', value: formatINRFull(results.totalRejected), sub: '15% rejection rate' },
        { label: 'Rejection Revenue Loss', value: `${formatINR(results.rejectionLoss)}`, sub: 'per month' },
        { label: 'Underused Insurance Loss', value: `${formatINR(results.underuseLoss)}`, sub: '10% don\'t use eligible benefits' },
      ]}
      comparison={{
        without: { label: 'Without Skydoc', value: results.approvedRevenue },
        with: { label: 'With Skydoc', value: results.revenueWith },
      }}
      description="Claim rejections from poor documentation, underbilling from missing codes, delayed submissions, and patients not using eligible insurance benefits. Skydoc validates documentation, auto-codes billing, and guides patients through insurance eligibility."
    />
  );
};

const CalculatorLayout = ({ isDark, inputs, setInputs, sliders, totalGain, percentGain, metrics, comparison, description }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="india-roi-calculator-layout">
      {/* Input Panel */}
      <div className={`rounded-2xl border p-5 lg:p-7 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? 'bg-sky-900/50' : 'bg-sky-100'}`}>
            <IndianRupee className="w-4 h-4 text-sky-500" />
          </div>
          <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Your Hospital Details
          </h3>
        </div>

        <div className="space-y-5">
          {sliders.map((slider) => (
            <SliderInput
              key={slider.key}
              label={slider.label}
              value={inputs[slider.key]}
              min={slider.min}
              max={slider.max}
              step={slider.step}
              unit={slider.unit}
              isDark={isDark}
              onChange={(v) => setInputs(prev => ({ ...prev, [slider.key]: Number(v) }))}
            />
          ))}
        </div>

        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`mt-4 flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'} transition-colors`}
          data-testid="india-roi-toggle-details"
        >
          {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showDetails ? 'Hide' : 'How is this calculated?'}
        </button>

        {showDetails && (
          <p className={`mt-3 text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {description}
          </p>
        )}
      </div>

      {/* Results Panel */}
      <div className="space-y-4">
        {/* Hero Result */}
        <div className="bg-gradient-to-br from-sky-600 to-sky-700 rounded-2xl p-5 lg:p-7 text-white">
          <p className="text-sky-200 text-xs font-medium mb-1">Monthly Revenue Uplift with Skydoc</p>
          <p className="text-3xl lg:text-4xl font-bold mb-2" data-testid="india-roi-total-gain">
            +{formatINR(totalGain)}
          </p>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-300" />
            <span className="text-sky-100 text-sm">{percentGain}% increase over current</span>
          </div>
        </div>

        {/* Comparison Bar */}
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Revenue Comparison</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{comparison.without.label}</span>
              <span className={`text-sm font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{formatINR(comparison.without.value)}</span>
            </div>
            <div className="relative h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700">
              <div className="absolute inset-y-0 left-0 bg-slate-400 rounded-full" style={{ width: comparison.with.value > 0 ? `${(comparison.without.value / comparison.with.value) * 100}%` : '0%' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>{comparison.with.label}</span>
              <span className="text-sm font-bold text-sky-500">{formatINR(comparison.with.value)}</span>
            </div>
            <div className="relative h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700">
              <div className="absolute inset-y-0 left-0 bg-sky-500 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((m, i) => (
            <div key={i} className={`rounded-xl border p-3 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
              <p className={`text-xs mb-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{m.label}</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{m.value}</p>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{m.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SliderInput = ({ label, value, min, max, step, unit, isDark, onChange }) => {
  const displayValue = unit === 'inr' ? `${formatINRFull(value)}` : unit === 'percent' ? `${value}%` : `${formatINRFull(value)} ${unit}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-xs font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{label}</span>
        <span className="text-xs font-bold text-sky-500">{displayValue}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-sky-500 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
        data-testid={`india-slider-${label.toLowerCase().replace(/[\s\/]+/g, '-')}`}
      />
      <div className={`flex justify-between text-xs mt-0.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
        <span>{unit === 'inr' ? `${formatINR(min)}` : min}</span>
        <span>{unit === 'inr' ? `${formatINR(max)}` : max}</span>
      </div>
    </div>
  );
};

const ROICalculatorIndia = ({ isDark }) => {
  const [activeTab, setActiveTab] = useState('followup');

  const renderCalculator = () => {
    switch (activeTab) {
      case 'followup': return <FollowUpCalculator isDark={isDark} />;
      case 'labs': return <LabsCalculator isDark={isDark} />;
      case 'procedures': return <ProceduresCalculator isDark={isDark} />;
      case 'insurance': return <InsuranceCalculator isDark={isDark} />;
      default: return <FollowUpCalculator isDark={isDark} />;
    }
  };

  return (
    <section
      id="india-calculator"
      className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-gradient-to-b from-white to-slate-50'}`}
      data-testid="roi-calculator-india-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-sky-900/50 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>
            <IndianRupee className="w-3.5 h-3.5" />
            India ROI Calculator
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            Revenue Leakage Skydoc Recovers
          </h2>
          <p className={`text-base lg:text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            See exactly how much revenue your hospital is losing monthly and how Skydoc's AI agents recover it. All figures in INR.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="india-roi-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/25'
                    : isDark
                      ? 'bg-slate-700/60 text-slate-300 hover:bg-slate-600/60'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
                data-testid={`india-roi-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Active Calculator */}
        {renderCalculator()}

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Want a customized ROI report for your hospital?
          </p>
          <a
            href="#demo"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-full text-sm font-semibold transition-all shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40"
            data-testid="india-roi-cta-btn"
          >
            Get Custom Analysis
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ROICalculatorIndia;
